import { action, atom, onConnect, reaction, sleep } from '@reatom/framework'

import { Result } from '@blade/std-lib'
import { SearchUser } from '@root/entities/search-user'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { ApiError, apiRequest } from '@root/shared/api'
import { reatomResourcePaginated } from '@root/shared/lib'

import type { PaginatedData } from '@root/shared/lib'

type SearchArgs = {
  query: string
  cursor?: string
  /**
   * @min 1
   * @max 50
   */
  limit?: number
}

export const searchUsersRequestAction = action(
  async (ctx, searchArgs: SearchArgs) => {
    const {
      tradeApiClient,
      environment: { assets },
    } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.user.userSearchList, [searchArgs]),
    )

    return result.pipe(
      Result.map((x) => ({
        data: x.data.map((x) => SearchUser.fromDto(x, assets.avatarsHost)),
        meta: x.meta,
      })),
      Result.mapError(ApiError.fallbackToUnknown),
    )
  },
  'searchUsersRequestAction',
)

export const reatomUsersSearch = (options: {
  debounce: number
  startSearchSymbols: number
}) => {
  const searchQueryAtom = atom('', 'searchQueryAtom')
  const debouncedSearchQueryAtom = atom('', 'debouncedSearchQueryAtom')

  const searchQueryDebounceReaction = reaction(async (ctx) => {
    const searchQuery = ctx.spy(searchQueryAtom)

    await ctx.schedule(() => sleep(options.debounce))

    // if (searchQuery.length < options.startSearchSymbols) {
    //   paginatedModel.reset(ctx)
    //   searchQueryAtom(ctx, '')
    // } else {
    debouncedSearchQueryAtom(ctx, searchQuery)
    // }
  }, 'searchQueryDebounceReaction')

  onConnect(searchQueryAtom, (ctx) => {
    const searchQueryDebounceReactionAtom = searchQueryDebounceReaction(ctx)

    return () => {
      searchQueryDebounceReactionAtom.unsubscribe()
    }
  })

  const paginatedModel = reatomResourcePaginated(
    async (ctx, { cursor, limit }) => {
      const query = ctx.spy(debouncedSearchQueryAtom)

      const requestResult = await ctx.schedule(() =>
        searchUsersRequestAction(ctx, {
          cursor,
          limit,
          query,
        }),
      )

      return Result.map(
        requestResult,
        (x): PaginatedData<SearchUser.SearchUser, 'cursor'> => ({
          data: x.data,
          hasNext: x.meta.hasNext,
          nextCursor: x.meta.nextCursor ?? 'no-no-no',
        }),
      )
    },
    // eslint-disable-next-line @reatom/unit-naming-rule
    {
      name: `search-users.paginated`,
      limit: 30,
      strategy: 'cursor',
      reconnectStrategy: 'refresh',
    },
  )

  return {
    paginatedModel,
    searchQueryAtom,
  }
}

export type UsersSearchModel = ReturnType<typeof reatomUsersSearch>
